/**
 * NativeUpdateModal
 *
 * Modal mostrato su iOS e Android quando è disponibile un aggiornamento nello store.
 * Il pulsante "Aggiorna ora" apre l'App Store (iOS) o il Play Store (Android).
 * Obbligatorio: nessuna via di fuga. Niente pulsante "Più tardi", niente dismiss
 * da backdrop, e il tasto indietro hardware Android non chiude il modal (nessun
 * onClose passato a useScrollLock → il back button non viene intercettato da questo
 * modal e ricade sul comportamento globale dell'app, senza mai chiudere il popup).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { useScrollLock } from '../../hooks/useScrollLock';
import { platform } from '../../utils/platform';
import { checkNativeUpdate } from '../../services/native/updateManager';
import { openExternalUrl } from '../../hooks/useNativeBrowser';

interface UpdateState {
  updateAvailable: boolean;
  storeUrl?: string;
}

export const NativeUpdateModal: React.FC = () => {
  const { t } = useTranslation();
  const [updateState, setUpdateState] = useState<UpdateState | null>(null);
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    if (!platform.isNative) return;

    const run = async () => {
      const result = await checkNativeUpdate();
      if (result?.updateAvailable) {
        setUpdateState(result);
      }
    };

    run();
  }, []);

  const isVisible = !!updateState?.updateAvailable && platform.isNative;

  useScrollLock(isVisible);

  const handleUpdate = useCallback(async () => {
    if (!updateState?.storeUrl) return;
    setIsOpening(true);
    try {
      await openExternalUrl(updateState.storeUrl);
    } finally {
      setIsOpening(false);
    }
  }, [updateState?.storeUrl]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/70 dark:bg-black/80 z-[9999] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 w-full max-w-md sm:max-w-lg overflow-hidden animate-scale-in border border-slate-100/60 dark:border-slate-800/70">

        {/* Body */}
        <div className="p-4 sm:p-6 flex flex-col items-center text-center">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 dark:bg-sky-900/30">
            <Download className="h-5 w-5 text-sky-500 dark:text-sky-400" aria-hidden="true" />
          </div>

          <div className="mt-4 flex-1 text-center w-full">
            <h3 className="text-lg sm:text-xl font-title font-semibold text-slate-900 dark:text-white">
              {t('native_update_modal.title', 'Aggiornamento richiesto')}
            </h3>

            <div className="mt-2">
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                {t(
                  'native_update_modal.description',
                  "Per continuare a usare Deskbay è necessario aggiornare all'ultima versione disponibile sullo store."
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 dark:bg-slate-900/80 px-4 sm:px-6 py-5 sm:py-6 flex flex-col gap-2 sm:gap-3 border-t border-slate-100 dark:border-slate-800/70">
          <Button
            onClick={handleUpdate}
            variant="primary"
            className="w-full gap-2 text-sm sm:text-base"
            disabled={isOpening}
          >
            {isOpening ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {t('native_update_modal.action_opening', 'Apertura...')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {t('native_update_modal.action_update', 'Aggiorna ora')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
